import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccessibilityTTS } from '@/hooks';
import { useConfigStore } from '@/stores';

export const useDropdownTTS = () => {
    const { t } = useTranslation();
    const { speak, getTextToSpeak } = useAccessibilityTTS();
    const voice = useConfigStore((state) => state?.config?.tts?.voice);

    // Speak only if TTS is enabled
    const speakIfEnabled = useCallback(
        (text: string) => {
            if (!voice) return;
            speak(text);
        },
        [voice, speak]
    );

    // Get TTS text for dropdown option on focus (normal accessibility TTS)
    const getFocusTTS = useCallback(
        (element: Element | null) => {
            if (!element) return '';
            return getTextToSpeak(element);
        },
        [getTextToSpeak]
    );

    // Get TTS text for dropdown option selection (custom message)
    const getSelectionTTS = useCallback(
        (optionLabel: string) => {
            return t('shop.search.tts.content.selected_building', { buildingName: optionLabel });
        },
        [t]
    );

    // Speak on focus (when navigating dropdown options)
    const speakOnFocus = useCallback(
        (element: Element | null) => {
            if (!voice || !element) return;
            const ttsText = getFocusTTS(element);
            if (ttsText) {
                speakIfEnabled(ttsText);
            }
        },
        [voice, getFocusTTS, speakIfEnabled]
    );

    // Speak on selection (when an option is selected)
    const speakOnSelection = useCallback(
        (optionLabel: string) => {
            if (!voice) return;
            const ttsText = getSelectionTTS(optionLabel);
            if (ttsText) {
                speakIfEnabled(ttsText);
            }
        },
        [voice, getSelectionTTS, speakIfEnabled]
    );

    return {
        speakIfEnabled,
        getFocusTTS,
        getSelectionTTS,
        speakOnFocus,
        speakOnSelection,
    };
};

export default useDropdownTTS;
